/**
 * Karbon360° v2 — CBAM goods & precursors.
 *
 * The goods are what actually crosses the border: each row is one CN code in
 * one reporting year, attached to a production process. The modal leans on the
 * server for every regulatory fact — typing 6+ digits of CN fires a debounced
 * /api/cbam/cn-lookup that answers "is this a CBAM good, which sector, what
 * default value and benchmark apply" — so the client never hardcodes a number
 * from the Official Journal.
 *
 * Precursors live inside the good's modal as editable rows. The API models
 * them as a sub-resource, so saving diffs the local rows against what was
 * loaded: removed ids → DELETE, rows with ids → PUT, new rows → POST — one
 * "Ruaj" click, however the rows were edited.
 *
 * Value-quality is surfaced where the choice is made: a precursor on default
 * values gets a warn "defaults" badge, unknown origin gets the punitive
 * Annex IV warning, EU origin shows the counts-as-zero hint. The table's
 * "Values" column is the server's per-good verdict from /api/cbam/summary —
 * the same quality rule the overview shows, never recomputed client-side.
 *
 * A precursor can also be PRODUCED IN THIS INSTALLATION (value_source
 * 'internal'): the row links a source good of the same reporting year in a
 * DIFFERENT production process, and the server resolves its SEE from that
 * good's own computed result at calculation time (Annex II point E(1) of
 * IR (EU) 2025/2547). The client offers only lawful candidates and mirrors
 * the two structural rules with keyed messages — no self-link, no
 * same-process link (Annex III point B) — but cycles and everything else
 * stay the server's job; it remains the boundary.
 *
 * LANGUAGE: entry-form labels Albanian; the table and the CN-lookup read-out
 * are analysis surfaces and stay English in both dictionaries (house split).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ErrorBoundary from '../../components/ErrorBoundary';
import { Shell } from '../../components/Shell';
import { X } from '../../components/v2/Icons';
import {
  PageHeader, Card, Button, Badge, Callout, Empty, Skeleton,
  Field, Input, Select, UnitInput, Modal, TableWrap, useToast, fmt,
} from '../../components/v2/ui';
import { useT } from '../../i18n';
import CbamGate from './CbamGate';
import {
  CBAM_BASE_YEAR, CBAM_YEARS, sectorLabel, ROUTES,
  PRECURSOR_COUNTRIES, isoFromCountry, CheckRow, num, QUALITY_TONE,
} from './cbam-shared';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const cnDigits = (v) => String(v || '').replace(/\s+/g, '');

const EMPTY_GOOD = {
  process_id: '', cn_code: '', description: '', year: CBAM_BASE_YEAR,
  production_route: '', production_tonnes: '', eu_export_tonnes: '',
  scrap_tonnes_per_tonne: '', pre_consumer_scrap_pct: '', non_al_elements_pct: '',
};

const EMPTY_PREC = {
  name: '', cn_code: '', tonnes_per_tonne: '', value_source: 'default', eu_origin: false,
  source_good_id: '',
  see_direct: '', see_indirect: '', supplier_name: '', supplier_country: '',
  reporting_period: '', origin_operator: '', origin_installation: '', origin_registry_id: '',
  evidence_note: '', _lookup: null, _lookupLoading: false,
};

/** English read-out of a cn-lookup `default` / `fallback_default` object. */
function DefaultLine({ d, year, fallback, t }) {
  if (!d) return null;
  return (
    <div style={{ fontSize: 12.5, color: 'var(--n700)', marginTop: 6 }}>
      <b>{fallback ? t('cbam.lookup.fallback') : t('cbam.lookup.default', { year })}:</b>{' '}
      <span className="k2-num">{fmt(num(d.base), 3)}</span> base →{' '}
      <span className="k2-num" style={{ fontWeight: 600 }}>{fmt(num(d.marked_up), 3)}</span> tCO₂e/t
      {d.markup_pct !== undefined && d.markup_pct !== null && <> (+{d.markup_pct}% mark-up)</>}
      {d.route && <> · route {d.route}</>}
      {d.source && <span style={{ color: 'var(--n500)' }}> · {d.source}</span>}
    </div>
  );
}

function CbamGoodsBody() {
  const { t, lang } = useT();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [goods, setGoods] = useState([]);
  const [installations, setInstallations] = useState([]);
  /**
   * The server's per-good quality verdict from /api/cbam/summary — the ONE
   * place the actual|mixed|defaults rule lives. null until the summary lands;
   * a good missing from it (just created) shows a dash, never a client guess.
   */
  const [serverQuality, setServerQuality] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [year, setYear] = useState(CBAM_BASE_YEAR);
  const [reloadKey, setReloadKey] = useState(0);

  const [editing, setEditing] = useState(null);    // null | {} (new) | good
  const [form, setForm] = useState(EMPTY_GOOD);
  const [precs, setPrecs] = useState([]);
  const [removedPrecIds, setRemovedPrecIds] = useState([]);
  const [lookup, setLookup] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  /**
   * True after a year change inside the modal wiped internal source links —
   * the keyed notice by the precursor list says why the pickers went empty.
   */
  const [yearClearNotice, setYearClearNotice] = useState(false);

  /** Guards late lookup responses against patching a modal opened afterwards. */
  const modalSeq = useRef(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/cbam/goods?year=${year}`, auth()).then((r) => r.data?.goods || []),
      axios.get(`${API_URL}/api/cbam/installations`, auth()).then((r) => r.data?.installations || []),
    ])
      .then(([g, ins]) => { if (!alive) return; setGoods(g); setInstallations(ins); })
      .catch((e) => { if (alive) setError(e.response?.data?.message || 'Could not load the goods.'); })
      .finally(() => { if (alive) setLoading(false); });
    // The quality column rides on its own request: a slow or failed summary
    // leaves the table usable with dashes, never a wrong badge or a dead page.
    setServerQuality(null);
    axios.get(`${API_URL}/api/cbam/summary?year=${year}`, auth())
      .then((r) => {
        if (!alive) return;
        const map = {};
        (r.data?.goods || []).forEach((g) => { if (g.quality) map[g.id] = g.quality; });
        setServerQuality(map);
      })
      .catch(() => { /* no verdict → the Values column stays a dash */ });
    return () => { alive = false; };
  }, [year, reloadKey]);
  const reload = () => setReloadKey((k) => k + 1);

  const processOptions = useMemo(
    () => installations.flatMap((i) => (i.processes || []).map((p) => ({ ...p, installation: i }))),
    [installations],
  );
  const selectedProcess = processOptions.find((p) => String(p.id) === String(form.process_id));
  const sector = (lookup && lookup.in_annex && lookup.sector) || selectedProcess?.sector || '';

  /**
   * Goods of the MODAL's reporting year — the pool an internal precursor may
   * link to. When the modal year matches the table filter, the page list is
   * already right; a different modal year fetches its own list, so the picker
   * is never silently filled with the wrong year's goods.
   */
  const [yearGoods, setYearGoods] = useState([]);
  useEffect(() => {
    if (!editing) return undefined;
    if (Number(form.year) === Number(year)) { setYearGoods(goods); return undefined; }
    let alive = true;
    axios.get(`${API_URL}/api/cbam/goods?year=${form.year}`, auth())
      .then((r) => { if (alive) setYearGoods(r.data?.goods || []); })
      .catch(() => { if (alive) setYearGoods([]); });
    return () => { alive = false; };
  }, [editing, form.year, year, goods]);

  /**
   * Lawful source-good candidates: same year, never the good being edited,
   * never a good of the SAME production process — inside one process the
   * internal flow carries no SEE×mass term (Annex III point B), so offering
   * the link would invite a row the server must reject.
   */
  const sourceGoodOptions = useMemo(
    () => yearGoods.filter((g) => (!editing?.id || Number(g.id) !== Number(editing.id))
      && String(g.process_id) !== String(form.process_id)),
    [yearGoods, editing, form.process_id],
  );
  const sourceGoodById = (id) => yearGoods.find((g) => Number(g.id) === Number(id)) || null;

  /* ── CN live lookup (good) ───────────────────────────────────────────── */

  useEffect(() => {
    if (!editing) return undefined;
    const code = cnDigits(form.cn_code);
    if (code.length < 6) { setLookup(null); setLookupLoading(false); return undefined; }
    let alive = true;
    setLookupLoading(true);
    const tmr = setTimeout(() => {
      const iso = isoFromCountry(selectedProcess?.installation?.country);
      const q = new URLSearchParams({ code, year: String(form.year) });
      if (iso) q.set('country', iso);
      axios.get(`${API_URL}/api/cbam/cn-lookup?${q.toString()}`, auth())
        .then((r) => { if (alive) setLookup(r.data); })
        .catch(() => { if (alive) setLookup(null); })
        .finally(() => { if (alive) setLookupLoading(false); });
    }, 400);
    return () => { alive = false; clearTimeout(tmr); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, form.cn_code, form.year, form.process_id]);

  /* ── modal open/close ────────────────────────────────────────────────── */

  const startNew = () => {
    modalSeq.current += 1;
    setForm({ ...EMPTY_GOOD, year });
    setPrecs([]);
    setRemovedPrecIds([]);
    setLookup(null);
    setFormError('');
    setYearClearNotice(false);
    setEditing({});
  };

  const startEdit = (g) => {
    modalSeq.current += 1;
    setForm({
      process_id: String(g.process_id ?? ''),
      cn_code: g.cn_code || '',
      description: g.description || '',
      year: Number(g.year) || CBAM_BASE_YEAR,
      production_route: g.production_route || '',
      production_tonnes: String(num(g.production_tonnes) ?? ''),
      eu_export_tonnes: g.eu_export_tonnes === null || g.eu_export_tonnes === undefined ? '' : String(num(g.eu_export_tonnes)),
      scrap_tonnes_per_tonne: g.scrap_tonnes_per_tonne == null ? '' : String(num(g.scrap_tonnes_per_tonne)),
      pre_consumer_scrap_pct: g.pre_consumer_scrap_pct == null ? '' : String(num(g.pre_consumer_scrap_pct)),
      non_al_elements_pct: g.non_al_elements_pct == null ? '' : String(num(g.non_al_elements_pct)),
    });
    setPrecs((g.precursors || []).map((p) => ({
      ...EMPTY_PREC,
      id: p.id,
      name: p.name || '',
      cn_code: p.cn_code || '',
      tonnes_per_tonne: String(num(p.tonnes_per_tonne) ?? ''),
      value_source: p.value_source || 'default',
      source_good_id: p.source_good_id == null ? '' : String(p.source_good_id),
      eu_origin: !!p.eu_origin,
      see_direct: p.see_direct == null ? '' : String(num(p.see_direct)),
      see_indirect: p.see_indirect == null ? '' : String(num(p.see_indirect)),
      supplier_name: p.supplier_name || '',
      supplier_country: p.supplier_country || '',
      reporting_period: p.reporting_period || '',
      origin_operator: p.origin_operator || '',
      origin_installation: p.origin_installation || '',
      origin_registry_id: p.origin_registry_id || '',
      evidence_note: p.evidence_note || '',
    })));
    setRemovedPrecIds([]);
    setLookup(null);
    setFormError('');
    setYearClearNotice(false);
    setEditing(g);
  };

  const closeModal = () => { modalSeq.current += 1; setEditing(null); };

  const setF = (patch) => setForm((v) => ({ ...v, ...patch }));
  const setPrec = (idx, patch) => setPrecs((v) => v.map((r, j) => (j === idx ? { ...r, ...patch } : r)));
  const removePrec = (idx) => {
    const row = precs[idx];
    if (row.id) setRemovedPrecIds((v) => [...v, row.id]);
    setPrecs((v) => v.filter((_, j) => j !== idx));
  };

  /**
   * A year change invalidates every internal source link: an internal
   * precursor must reference a good of the SAME reporting year (Annex II
   * point E(1) of IR (EU) 2025/2547), and a source picked under the old year
   * is gone from yearGoods — the mirror checks can't see it, so the user
   * would only learn at the server round-trip. Clear the stale links at once
   * and say why; a year change that wiped nothing stays silent.
   */
  const pickYear = (val) => {
    const y = Number(val);
    if (y === Number(form.year)) return;
    setF({ year: y });
    if (precs.some((r) => r.value_source === 'internal' && r.source_good_id)) {
      setPrecs((v) => v.map((r) => (
        r.value_source === 'internal' && r.source_good_id ? { ...r, source_good_id: '' } : r
      )));
      setYearClearNotice(true);
    }
  };

  const pickProcess = (val) => {
    const p = processOptions.find((x) => String(x.id) === String(val));
    setForm((v) => ({
      ...v,
      process_id: val,
      // Prefill the route from the process but never clobber an explicit choice.
      production_route: v.production_route || p?.production_route || '',
    }));
  };

  /* ── per-precursor default resolution ────────────────────────────────── */

  const lookupPrec = (idx, row) => {
    const code = cnDigits(row.cn_code);
    const c = row.supplier_country;
    if (row.value_source !== 'default' || row.eu_origin || code.length < 6 || !c || c === 'unknown') {
      setPrec(idx, { _lookup: null, _lookupLoading: false });
      return;
    }
    const seq = modalSeq.current;
    setPrec(idx, { _lookupLoading: true });
    const q = new URLSearchParams({ code, year: String(form.year) });
    if (c !== 'other') q.set('country', c);
    axios.get(`${API_URL}/api/cbam/cn-lookup?${q.toString()}`, auth())
      .then((r) => { if (modalSeq.current === seq) setPrec(idx, { _lookup: r.data, _lookupLoading: false }); })
      .catch(() => { if (modalSeq.current === seq) setPrec(idx, { _lookup: null, _lookupLoading: false }); });
  };

  /* ── save / delete ───────────────────────────────────────────────────── */

  const precPayload = (r) => {
    // An internal row sends ONLY the contract's internal shape. The server
    // nulls the supplier fields and SEE inputs, forces eu_origin false and
    // copies cn_code from the source good — the client never pretends to own
    // values the linked good's calculation will provide.
    if (r.value_source === 'internal') {
      return {
        value_source: 'internal',
        source_good_id: Number(r.source_good_id),
        name: r.name.trim(),
        tonnes_per_tonne: Number(r.tonnes_per_tonne),
        evidence_note: r.evidence_note.trim() || null,
      };
    }
    return {
      name: r.name.trim(),
      cn_code: cnDigits(r.cn_code) || null,
      tonnes_per_tonne: Number(r.tonnes_per_tonne),
      value_source: r.value_source,
      eu_origin: !!r.eu_origin,
      see_direct: r.value_source === 'supplier' ? num(r.see_direct) : null,
      see_indirect: r.value_source === 'supplier' ? num(r.see_indirect) : null,
      supplier_name: r.supplier_name.trim() || null,
      supplier_country: r.supplier_country.trim() || null,
      reporting_period: r.reporting_period.trim() || null,
      origin_operator: r.origin_operator.trim() || null,
      origin_installation: r.origin_installation.trim() || null,
      origin_registry_id: r.origin_registry_id.trim() || null,
      evidence_note: r.evidence_note.trim() || null,
    };
  };

  const save = async () => {
    const cn = cnDigits(form.cn_code);
    // Rows the user added but never touched are dropped silently, not rejected.
    const rows = precs.filter((r) => r.name.trim() || r.cn_code.trim() || r.tonnes_per_tonne !== '' || r.source_good_id);
    if (!form.process_id) { setFormError(t('cbam.goods.vProcess')); return; }
    if (cn.length < 6) { setFormError(t('cbam.goods.vCn')); return; }
    if (!(Number(form.production_tonnes) > 0)) { setFormError(t('cbam.goods.vProduction')); return; }
    if (rows.some((r) => !r.name.trim() || !(Number(r.tonnes_per_tonne) > 0))) {
      setFormError(t('cbam.goods.vPrec')); return;
    }
    // Internal rows: mirror the server's structural rules so the message is
    // immediate and in the user's language — the picker already filters these
    // out, so they only fire on stale state (e.g. the good's process changed
    // after the source was picked). Cycles only the server can see.
    const internals = rows.filter((r) => r.value_source === 'internal');
    if (internals.some((r) => !r.source_good_id)) {
      setFormError(t('cbam.goods.vPrecSource')); return;
    }
    if (editing?.id && internals.some((r) => Number(r.source_good_id) === Number(editing.id))) {
      setFormError(t('cbam.goods.vPrecSelf')); return;
    }
    if (internals.some((r) => {
      const sg = sourceGoodById(r.source_good_id);
      return sg && String(sg.process_id) === String(form.process_id);
    })) {
      setFormError(t('cbam.goods.vPrecSameProcess')); return;
    }
    // An unset country on a default-valued row would reach the API as
    // supplier_country '' — "origin unidentifiable", which silently lands the
    // punitive Annex IV default. The choice must be explicit: a country, or
    // "Unknown origin" with its warning read on screen.
    if (rows.some((r) => !r.eu_origin && r.value_source === 'default' && !r.supplier_country.trim())) {
      setFormError(t('cbam.goods.vPrecCountry')); return;
    }
    setSaving(true); setFormError('');
    try {
      const payload = {
        process_id: Number(form.process_id),
        cn_code: cn,
        description: form.description.trim() || null,
        year: Number(form.year),
        production_tonnes: Number(form.production_tonnes),
        eu_export_tonnes: num(form.eu_export_tonnes),
        production_route: form.production_route || null,
        scrap_tonnes_per_tonne: sector === 'aluminium' ? num(form.scrap_tonnes_per_tonne) : null,
        pre_consumer_scrap_pct: sector === 'aluminium' ? num(form.pre_consumer_scrap_pct) : null,
        non_al_elements_pct: sector === 'aluminium' ? num(form.non_al_elements_pct) : null,
      };
      let goodId = editing?.id;
      if (goodId) {
        await axios.put(`${API_URL}/api/cbam/goods/${goodId}`, payload, auth());
      } else {
        const r = await axios.post(`${API_URL}/api/cbam/goods`, payload, auth());
        goodId = r.data?.good?.id ?? r.data?.id;
        // Adopt the created id at once: if a precursor call below fails, the
        // modal stays open and the next "Ruaj" must PUT this good, not POST a twin.
        if (goodId) setEditing((prev) => (prev ? { ...prev, id: goodId } : prev));
      }
      if (goodId) {
        // Sync precursors row by row, collecting failures instead of aborting:
        // a mid-loop error would otherwise leave the server half-updated with
        // no word on what landed. Each id leaves removedPrecIds as its DELETE
        // succeeds, so a retry never re-deletes an already-gone row; created
        // rows adopt their id for the same reason — retry PUTs, never re-POSTs.
        const failed = [];
        for (const pid of removedPrecIds) {
          try {
            await axios.delete(`${API_URL}/api/cbam/goods/${goodId}/precursors/${pid}`, auth());
            setRemovedPrecIds((v) => v.filter((x) => x !== pid));
          } catch {
            failed.push(`${t('cbam.prec.remove')} #${pid}`);
          }
        }
        for (const [i, row] of rows.entries()) {
          try {
            if (row.id) {
              await axios.put(`${API_URL}/api/cbam/goods/${goodId}/precursors/${row.id}`, precPayload(row), auth());
            } else {
              const pr = await axios.post(`${API_URL}/api/cbam/goods/${goodId}/precursors`, precPayload(row), auth());
              const pid = pr.data?.precursor?.id ?? pr.data?.id;
              if (pid) setPrecs((v) => v.map((x) => (x === row ? { ...x, id: pid } : x)));
            }
          } catch {
            failed.push(row.name.trim() || `${t('cbam.prec.name')} ${i + 1}`);
          }
        }
        if (failed.length > 0) {
          setFormError(t('cbam.goods.precSyncFailed', { rows: failed.join(', ') }));
          return;
        }
      }
      toast(t('cbam.goods.saved'));
      closeModal();
      reload();
    } catch (e) {
      setFormError(e.response?.data?.message || t('cbam.goods.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const removeGood = async (g) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('cbam.goods.confirmDelete', { cn: g.cn_code, year: g.year }))) return;
    try {
      await axios.delete(`${API_URL}/api/cbam/goods/${g.id}`, auth());
      toast(t('cbam.goods.deleted'));
      reload();
    } catch (e) {
      toast(e.response?.data?.message || t('cbam.goods.saveFailed'), { tone: 'err' });
    }
  };

  /* ── render ──────────────────────────────────────────────────────────── */

  if (error) return <Callout tone="err">{error}</Callout>;

  if (loading) {
    return (
      <Card>
        <div style={{ display: 'grid', gap: 10 }}>{[0, 1, 2].map((i) => <Skeleton key={i} h={30} />)}</div>
      </Card>
    );
  }

  const procName = (id) => {
    const p = processOptions.find((x) => String(x.id) === String(id));
    return p ? `${p.installation.name} · ${p.name}` : '—';
  };

  const countryLabel = (c) => (lang === 'en' ? c.en : c.sq);

  return (
    <>
      {processOptions.length === 0 && (
        <div style={{ marginBottom: 16 }}>
          <Callout tone="warn">
            {t('cbam.goods.needProcess')}{' '}
            <button type="button" className="k2-lnk" onClick={() => navigate('/cbam/installations')}>
              {t('cbam.goods.toInstallations')} →
            </button>
          </Callout>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button icon={X.Plus} variant="mint" onClick={startNew} disabled={processOptions.length === 0}>
          {t('cbam.goods.new')}
        </Button>
      </div>

      {goods.length === 0 ? (
        <Card>
          <Empty
            icon={X.Layers}
            title={t('cbam.goods.empty')}
            body={t('cbam.goods.emptyBody')}
            action={processOptions.length > 0 && (
              <Button variant="mint" icon={X.Plus} onClick={startNew}>{t('cbam.goods.new')}</Button>
            )}
          />
          <div style={{ marginTop: 14, display: 'flex', justifyContent: 'center' }}>
            <Select value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ width: 120 }}>
              {CBAM_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </Select>
          </div>
        </Card>
      ) : (
        <TableWrap
          toolbar={
            <>
              <Select value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ width: 110 }}>
                {CBAM_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </Select>
              <span style={{ fontSize: 12.5, color: 'var(--n500)', marginLeft: 'auto' }}>
                {goods.length} goods · reporting year {year}
              </span>
            </>
          }
        >
          <table className="k2-t">
            <thead>
              <tr>
                <th>CN code</th>
                <th>Description</th>
                <th>Category</th>
                <th className="k2-r">Route</th>
                <th className="k2-r">Production</th>
                <th className="k2-r">EU export</th>
                <th>Process</th>
                <th>Values</th>
                <th style={{ width: 44 }} />
              </tr>
            </thead>
            <tbody>
              {goods.map((g) => {
                const q = serverQuality ? serverQuality[g.id] : null;
                return (
                  <tr key={g.id} onClick={() => startEdit(g)} style={{ cursor: 'pointer' }}>
                    <td className="k2-num" style={{ fontWeight: 600 }}>{g.cn_code}</td>
                    <td>
                      <span className="k2-nm">{g.description || '—'}</span>
                      <span className="k2-sub2">{sectorLabel(g.sector)}</span>
                    </td>
                    <td>{g.goods_category ? <Badge tone="out">{g.goods_category}</Badge> : '—'}</td>
                    <td className="k2-r k2-num">{g.production_route || '—'}</td>
                    <td className="k2-r k2-num">{fmt(num(g.production_tonnes), 1)} <span style={{ fontSize: 11, color: 'var(--n400)' }}>t</span></td>
                    <td className="k2-r k2-num">{g.eu_export_tonnes == null ? '—' : <>{fmt(num(g.eu_export_tonnes), 1)} <span style={{ fontSize: 11, color: 'var(--n400)' }}>t</span></>}</td>
                    <td style={{ fontSize: 12.5, color: 'var(--n600)' }}>{procName(g.process_id)}</td>
                    <td>{q ? <Badge tone={QUALITY_TONE[q] || 'out'}>{q}</Badge> : '—'}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="k2-rowact">
                        <Button size="sm" variant="gh" iconOnly icon={X.Close} aria-label={`Delete ${g.cn_code}`} onClick={() => removeGood(g)} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableWrap>
      )}

      {/* ── create / edit good ── */}
      {editing && (
        <Modal
          title={editing.id ? t('cbam.goods.modalEdit') : t('cbam.goods.modalNew')}
          wide
          onClose={closeModal}
          footer={
            <>
              <Button onClick={closeModal}>{t('cbam.form.cancel')}</Button>
              <Button variant="mint" icon={X.Check} disabled={saving} onClick={save}>
                {saving ? t('entry.posting') : t('cbam.goods.saveCta')}
              </Button>
            </>
          }
        >
          {formError && <div style={{ marginBottom: 14 }}><Callout tone="err">{formError}</Callout></div>}

          <div className="k2-g2">
            <Field label={t('cbam.goods.process')} required>
              <Select value={form.process_id} onChange={(e) => pickProcess(e.target.value)}>
                <option value="">—</option>
                {processOptions.map((p) => (
                  <option key={p.id} value={p.id}>{p.installation.name} · {p.name}</option>
                ))}
              </Select>
            </Field>
            <Field label={t('cbam.goods.year')}>
              <Select value={form.year} onChange={(e) => pickYear(e.target.value)}>
                {CBAM_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </Select>
            </Field>
            <Field label={t('cbam.goods.cn')} required hint={t('cbam.goods.cnHint')}>
              <Input
                value={form.cn_code}
                onChange={(e) => setF({ cn_code: e.target.value.replace(/[^\d\s]/g, '') })}
                placeholder="7610 10 00"
                autoFocus={!editing.id}
              />
            </Field>
            <Field label={t('cbam.goods.description')} optional>
              <Input value={form.description} onChange={(e) => setF({ description: e.target.value })} placeholder={t('cbam.goods.descriptionPh')} />
            </Field>
          </div>

          {/* CN lookup read-out — English: this is the regulation talking, and it feeds English exports. */}
          {(lookupLoading || lookup) && (
            <div style={{ margin: '2px 0 14px', padding: '10px 12px', border: '1px solid var(--n200)', borderRadius: 'var(--r-md)', background: 'var(--n25)' }}>
              {lookupLoading ? (
                <span style={{ fontSize: 12.5, color: 'var(--n500)' }}>{t('cbam.lookup.checking')}</span>
              ) : lookup.in_annex ? (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                    <Badge tone="ok" dot>{t('cbam.lookup.inAnnex')}</Badge>
                    <Badge tone="dark">{sectorLabel(lookup.sector)}</Badge>
                    {lookup.goods_category && <Badge tone="out">{lookup.goods_category}</Badge>}
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--n600)', marginTop: 6 }}>
                    {lookup.chargeable_indirect ? t('cbam.lookup.indirectCharged') : t('cbam.lookup.indirectMemo')}
                  </div>
                  {lookup.boundary_hint && (
                    <div style={{ fontSize: 12.5, color: 'var(--n600)', marginTop: 4 }}>{lookup.boundary_hint}</div>
                  )}
                  {lookup.default
                    ? <DefaultLine d={lookup.default} year={form.year} t={t} />
                    : <DefaultLine d={lookup.fallback_default} year={form.year} fallback t={t} />}
                  {lookup.benchmark && (
                    <div style={{ fontSize: 12.5, color: 'var(--n700)', marginTop: 4 }}>
                      <b>{t('cbam.lookup.benchmark')}:</b>{' '}
                      {lookup.benchmark.K != null && <>K <span className="k2-num">{fmt(num(lookup.benchmark.K), 3)}</span></>}
                      {lookup.benchmark.K != null && lookup.benchmark.L != null && ' / '}
                      {lookup.benchmark.L != null && <>L <span className="k2-num">{fmt(num(lookup.benchmark.L), 3)}</span></>}
                      {' '}tCO₂e/t
                    </div>
                  )}
                </>
              ) : (
                <Callout tone="warn">{t('cbam.lookup.notInAnnex', { code: cnDigits(form.cn_code) })}</Callout>
              )}
            </div>
          )}

          <div className="k2-g2">
            <Field
              label={t('cbam.goods.route')}
              hint={lookup?.default?.route && form.production_route && lookup.default.route !== form.production_route
                ? t('cbam.goods.routeMismatch', { route: lookup.default.route })
                : undefined}
              hintTone={lookup?.default?.route && form.production_route && lookup.default.route !== form.production_route ? 'err' : undefined}
            >
              <Select value={form.production_route} onChange={(e) => setF({ production_route: e.target.value })}>
                <option value="">—</option>
                {ROUTES.map((r) => <option key={r} value={r}>{t(`cbam.route.${r}`)}</option>)}
              </Select>
            </Field>
            <div />
            <Field label={t('cbam.goods.production')} required>
              <UnitInput unit="t" type="number" min="0" step="0.001" value={form.production_tonnes} onChange={(e) => setF({ production_tonnes: e.target.value })} />
            </Field>
            <Field label={t('cbam.goods.euExport')} optional>
              <UnitInput unit="t" type="number" min="0" step="0.001" value={form.eu_export_tonnes} onChange={(e) => setF({ eu_export_tonnes: e.target.value })} />
            </Field>
          </div>

          {/* Aluminium sector parameters — Annex IV point 1.1(vii) of IR 2025/2547 wants them reported. */}
          {sector === 'aluminium' && (
            <div style={{ margin: '2px 0 14px', padding: '12px 12px 4px', border: '1px solid var(--n200)', borderRadius: 'var(--r-md)' }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--n500)', marginBottom: 10 }}>
                {t('cbam.goods.alParams')}
              </div>
              <div className="k2-g2">
                <Field label={t('cbam.goods.scrap')} optional>
                  <UnitInput unit="t/t" type="number" min="0" step="0.001" value={form.scrap_tonnes_per_tonne} onChange={(e) => setF({ scrap_tonnes_per_tonne: e.target.value })} />
                </Field>
                <Field label={t('cbam.goods.preConsumer')} optional>
                  <UnitInput unit="%" type="number" min="0" max="100" step="0.1" value={form.pre_consumer_scrap_pct} onChange={(e) => setF({ pre_consumer_scrap_pct: e.target.value })} />
                </Field>
                <Field label={t('cbam.goods.nonAl')} optional hint={t('cbam.goods.nonAlHint')}>
                  <UnitInput unit="%" type="number" min="0" max="100" step="0.1" value={form.non_al_elements_pct} onChange={(e) => setF({ non_al_elements_pct: e.target.value })} />
                </Field>
              </div>
            </div>
          )}

          {/* ── precursors ── */}
          <div style={{ borderTop: '1px solid var(--n100)', paddingTop: 12 }}>
            <div style={{ marginBottom: 2 }}>
              <span style={{ fontSize: 13.5, fontWeight: 600 }}>{t('cbam.goods.precursors')}</span>
            </div>
            <p style={{ margin: '0 0 12px', fontSize: 12.5, color: 'var(--n500)' }}>{t('cbam.goods.precursorsSub')}</p>

            {yearClearNotice && (
              <div style={{ marginBottom: 10 }}>
                <Callout tone="warn">{t('cbam.goods.yearClearedSources')}</Callout>
              </div>
            )}

            {precs.map((r, i) => {
              const rowInternal = r.value_source === 'internal';
              const rowDefault = r.value_source === 'default' && !r.eu_origin;
              const srcGood = rowInternal ? sourceGoodById(r.source_good_id) : null;
              return (
                <div key={r.id || `new-${i}`} style={{ border: '1px solid var(--n200)', borderRadius: 'var(--r-md)', padding: 14, marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--n700)' }}>
                      {r.name.trim() || `${t('cbam.prec.name')} ${i + 1}`}
                    </span>
                    {rowInternal
                      ? <Badge tone="ok">{t('cbam.prec.internalShort')}</Badge>
                      : r.eu_origin
                        ? <Badge tone="ok">SEE 0</Badge>
                        : rowDefault
                          ? <Badge tone="warn">defaults</Badge>
                          : <Badge tone="ok">supplier</Badge>}
                    {rowInternal && srcGood && (
                      <span className="k2-num" style={{ fontSize: 11.5, color: 'var(--n500)' }}>CN {srcGood.cn_code}</span>
                    )}
                    <span style={{ marginLeft: 'auto' }}>
                      <Button size="sm" variant="gh" iconOnly icon={X.Close} aria-label={t('cbam.prec.remove')} onClick={() => removePrec(i)} />
                    </span>
                  </div>

                  <div className="k2-g2">
                    <Field label={t('cbam.prec.name')} required>
                      <Input value={r.name} onChange={(e) => setPrec(i, { name: e.target.value })} placeholder={t('cbam.prec.namePh')} />
                    </Field>
                    {/* An internal row's CN is the SOURCE good's CN — the server
                        copies it on save, so the field only shows, never asks. */}
                    <Field label={t('cbam.prec.cn')} hint={rowInternal ? t('cbam.prec.internalCnHint') : undefined}>
                      {rowInternal ? (
                        <Input value={srcGood?.cn_code || ''} readOnly disabled />
                      ) : (
                        <Input
                          value={r.cn_code}
                          onChange={(e) => setPrec(i, { cn_code: e.target.value.replace(/[^\d\s]/g, '') })}
                          onBlur={() => lookupPrec(i, precs[i])}
                          placeholder="7604"
                        />
                      )}
                    </Field>
                    <Field label={t('cbam.prec.tpt')} required hint={rowInternal ? t('cbam.prec.tptInternalHint') : t('cbam.prec.tptHint')}>
                      <UnitInput unit="t/t" type="number" min="0" step="0.0001" value={r.tonnes_per_tonne} onChange={(e) => setPrec(i, { tonnes_per_tonne: e.target.value })} />
                    </Field>
                  </div>

                  {/* eu_origin has no meaning for an internal row (the contract
                      forces it false), so the checkbox hides rather than tempts. */}
                  {!rowInternal && (
                    <CheckRow
                      checked={r.eu_origin}
                      onChange={(v) => { setPrec(i, { eu_origin: v, _lookup: null }); }}
                      label={t('cbam.prec.euOrigin')}
                      hint={t('cbam.prec.euOriginHint')}
                      style={{ marginBottom: 10 }}
                    />
                  )}

                  {!r.eu_origin && (
                    <>
                      <div className="k2-bgroup" style={{ marginBottom: 12 }}>
                        <Button
                          size="sm"
                          className={r.value_source === 'supplier' ? 'k2-on' : undefined}
                          onClick={() => setPrec(i, { value_source: 'supplier', _lookup: null })}
                        >
                          {t('cbam.prec.supplier')}
                        </Button>
                        <Button
                          size="sm"
                          className={r.value_source === 'default' ? 'k2-on' : undefined}
                          onClick={() => {
                            const next = { ...precs[i], value_source: 'default' };
                            setPrec(i, { value_source: 'default' });
                            lookupPrec(i, next);
                          }}
                        >
                          {t('cbam.prec.default')}
                        </Button>
                        <Button
                          size="sm"
                          className={rowInternal ? 'k2-on' : undefined}
                          onClick={() => setPrec(i, { value_source: 'internal', eu_origin: false, _lookup: null })}
                        >
                          {t('cbam.prec.internal')}
                        </Button>
                      </div>

                      {rowInternal ? (
                        <>
                          <div className="k2-g2">
                            <Field label={t('cbam.prec.sourceGood')} required>
                              <Select value={r.source_good_id} onChange={(e) => setPrec(i, { source_good_id: e.target.value })}>
                                <option value="">—</option>
                                {sourceGoodOptions.map((g) => (
                                  <option key={g.id} value={g.id}>
                                    CN {g.cn_code} — {g.description || g.goods_category || ''}
                                  </option>
                                ))}
                              </Select>
                            </Field>
                            <Field label={t('cbam.prec.evidence')} hint={t('cbam.prec.internalEvidenceHint')}>
                              <Input value={r.evidence_note} onChange={(e) => setPrec(i, { evidence_note: e.target.value })} />
                            </Field>
                          </div>
                          {sourceGoodOptions.length === 0 && (
                            <div style={{ marginBottom: 8 }}>
                              <Callout tone="warn">{t('cbam.prec.noSourceGoods', { year: form.year })}</Callout>
                            </div>
                          )}
                          <Callout tone="info">{t('cbam.prec.internalHint')}</Callout>
                        </>
                      ) : r.value_source === 'supplier' ? (
                        <>
                          <div className="k2-g2">
                            <Field label={t('cbam.prec.seeDirect')}>
                              <UnitInput unit="tCO₂e/t" type="number" min="0" step="0.001" value={r.see_direct} onChange={(e) => setPrec(i, { see_direct: e.target.value })} />
                            </Field>
                            <Field label={t('cbam.prec.seeIndirect')}>
                              <UnitInput unit="tCO₂e/t" type="number" min="0" step="0.001" value={r.see_indirect} onChange={(e) => setPrec(i, { see_indirect: e.target.value })} />
                            </Field>
                            <Field label={t('cbam.prec.supplierName')}>
                              <Input value={r.supplier_name} onChange={(e) => setPrec(i, { supplier_name: e.target.value })} />
                            </Field>
                            <Field label={t('cbam.prec.supplierCountry')}>
                              <Input value={r.supplier_country} onChange={(e) => setPrec(i, { supplier_country: e.target.value })} />
                            </Field>
                            <Field label={t('cbam.prec.period')} hint={t('cbam.prec.periodHint')}>
                              <Input value={r.reporting_period} onChange={(e) => setPrec(i, { reporting_period: e.target.value })} placeholder={String(form.year)} />
                            </Field>
                            <Field label={t('cbam.prec.originOperator')}>
                              <Input value={r.origin_operator} onChange={(e) => setPrec(i, { origin_operator: e.target.value })} />
                            </Field>
                            <Field label={t('cbam.prec.originInstallation')}>
                              <Input value={r.origin_installation} onChange={(e) => setPrec(i, { origin_installation: e.target.value })} />
                            </Field>
                            <Field label={t('cbam.prec.originRegistryId')}>
                              <Input value={r.origin_registry_id} onChange={(e) => setPrec(i, { origin_registry_id: e.target.value })} />
                            </Field>
                            <Field label={t('cbam.prec.evidence')} hint={t('cbam.prec.evidenceHint')}>
                              <Input value={r.evidence_note} onChange={(e) => setPrec(i, { evidence_note: e.target.value })} />
                            </Field>
                          </div>
                          <Callout tone="info">{t('cbam.prec.supplierHint')}</Callout>
                        </>
                      ) : (
                        <>
                          <div className="k2-g2">
                            <Field label={t('cbam.prec.country')}>
                              <Select
                                value={r.supplier_country}
                                onChange={(e) => {
                                  const next = { ...precs[i], supplier_country: e.target.value };
                                  setPrec(i, { supplier_country: e.target.value });
                                  lookupPrec(i, next);
                                }}
                              >
                                <option value="">—</option>
                                {PRECURSOR_COUNTRIES.map((c) => <option key={c.value} value={c.value}>{countryLabel(c)}</option>)}
                              </Select>
                            </Field>
                          </div>
                          {r.supplier_country === 'unknown' && (
                            <Callout tone="warn">{t('cbam.prec.unknownWarn')}</Callout>
                          )}
                          {r._lookupLoading && <span style={{ fontSize: 12.5, color: 'var(--n500)' }}>{t('cbam.lookup.checking')}</span>}
                          {!r._lookupLoading && r._lookup && r.supplier_country && r.supplier_country !== 'unknown' && (
                            r._lookup.in_annex ? (
                              r._lookup.default
                                ? <DefaultLine d={r._lookup.default} year={form.year} t={t} />
                                : <DefaultLine d={r._lookup.fallback_default} year={form.year} fallback t={t} />
                            ) : (
                              <Callout tone="warn">{t('cbam.lookup.notInAnnex', { code: cnDigits(r.cn_code) })}</Callout>
                            )
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              );
            })}

            <Button size="sm" icon={X.Plus} onClick={() => setPrecs((v) => [...v, { ...EMPTY_PREC }])}>
              {t('cbam.goods.addPrecursor')}
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}

function CbamGoodsScreen() {
  const { t } = useT();
  return (
    <div className="k2-wrap">
      <PageHeader title={t('cbam.goods.title')} desc={t('cbam.goods.desc')} />
      <CbamGate>
        <CbamGoodsBody />
      </CbamGate>
    </div>
  );
}

export default function CbamGoodsPage() {
  return (
    <ErrorBoundary>
      <Shell>
        <CbamGoodsScreen />
      </Shell>
    </ErrorBoundary>
  );
}
